import {
  Plus,
  Settings,
  FileText,
  Code,
  Building,
  UserPlus,
  UserMinus,
  CreditCard,
  Mail,
  Phone,
  ChevronRight,
  Check,
  Trash2,
} from 'lucide-react'
import { useEffect, useState, memo, useRef, useCallback } from 'react'
import { useSearchParams } from 'react-router'
import { Toaster, toast } from 'sonner'
import DocumentManager from '@/components/DocumentManager'
import LangflowManager from '@/components/langflow/LangflowManager'
import ModelManager from '@/components/dashboard/ModelManager'
import OverlayTopBar from '@/components/OverlayTopBar/OverlayTopBar'
import PulsingSquareLoader from '@/components/UIUX/PulsingSquareLoader'; // Added import
import ReleaseNotes from '@/components/dashboard/ReleaseNotes';
import ReleaseNotesViewer from '@/components/dashboard/ReleaseNotesViewer'; // New import
import SpechopsAnimation from '@/components/SpechopsAnimation/SpechopsAnimation'
import { AccountSettings } from '@/components/dashboard/AccountSettings'
import SubscriptionManager from '@/components/dashboard/SubscriptionManager'
import NewProjectForm from '@/components/dashboard/NewProjectForm'
import { ProjectCard } from '@/components/dashboard/ProjectCard'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { Textarea } from '@/components/ui/textarea'
import { useToast } from '@/components/ui/use-toast'
import { useUser } from '@/context/UserContext'
import { useAccountStore } from '@/stores/accountStore';
import {
  useGetOrganizations,
  useCreateOrganization,
  useGetOrganization,
  useUpdateOrganization,
  useUpdateOrganizationBilling,
  useUpdateOrganizationContact,
  useDeleteOrganization,
  useGetOrganizationMembers,
  useAddMemberToOrganization,
  useRemoveMemberFromOrganization,
  useSetPrimaryOrganization,
  Organization
} from '@/stores/organizationsStore'
import {
  useGetProjects,
  useDeleteProject,
  useGetProject,
} from '@/stores/projectsStore'
import styles from '@/styles/Dashboard.module.css'
import { getVersionString } from '@/utils/version'
import releaseNotesData from '../../public/ReleaseNotes.json';
import { HookResult, createDefaultHookResult } from '../hooks/hookTypes'





// Array of available color classes
const colorClasses = [
  'color-red',
  'color-blue',
  'color-green',
  'color-purple',
  'color-orange',
  'color-pink',
  'color-teal',
  'color-yellow',
]

// Function to get a random color class
const getRandomColorClass = () => {
  const randomIndex = Math.floor(Math.random() * colorClasses.length)
  return colorClasses[randomIndex]
}

// Organization card component
const OrganizationCard = ({ organization, onSelect, onSetPrimary, onDelete, isPrimary }) => {
  // Use a random color class
  const [colorClass] = useState(getRandomColorClass)

  return (
    <div className={`${styles.project_card_wrapper} ${styles[colorClass]} ${styles.gentleWaveAnimation}`}>
      <Card
        className={`${styles.orgCard} ${isPrimary ? styles.primaryOrgCard : ''} ${styles.projectCardAnimated} ${styles.colorful}`}
      >
        <CardHeader>
          <CardTitle className="flex justify-between items-center">
            <span className="truncate">{organization.name}</span>
            {isPrimary && <Badge>Primary</Badge>}
          </CardTitle>
          <CardDescription>
            {organization.generalInformation || 'No description available'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <div className="flex items-center">
              <Mail className="mr-2 h-4 w-4" />
              <span>{organization.contactEmail || 'No contact email'}</span>
            </div>
            {organization.contactPhone && (
              <div className="flex items-center">
                <Phone className="mr-2 h-4 w-4" />
                <span>{organization.contactPhone}</span>
              </div>
            )}
            <div className="flex items-center">
              <CreditCard className="mr-2 h-4 w-4" />
              <span>{organization.billingPlan || 'Free'}</span>
            </div>
          </div>
        </CardContent>
        <CardFooter className="flex justify-between items-center gap-2">
          <Button variant="outline" onClick={() => onSelect(organization)}>
            Manage <ChevronRight className="ml-1 h-4 w-4" />
          </Button>
          <div className="flex items-center gap-2">
            {!isPrimary && (
              <Button
                variant="secondary"
                onClick={() => onSetPrimary(organization.id)}
              >
                Set Primary
              </Button>
            )}
            <Button
              variant="destructive"
              size="sm"
              onClick={() => onDelete(organization.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </CardFooter>
      </Card>
    </div>
  );
};

// New Organization Form Component
const NewOrganizationForm = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    generalInformation: '',
    contactEmail: '',
    contactPhone: '',
    website: '',
    billingEmail: '',
    billingName: ''
  });
  const [formErrors, setFormErrors] = useState({
    website: ''
  });

  const createOrganization = useCreateOrganization() || createDefaultHookResult();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });

    // Clear error when field is modified
    if (name === 'website' && formErrors.website) {
      setFormErrors({
        ...formErrors,
        website: ''
      });
    }
  };

  const validateWebsite = (url) => {
    if (!url) return true; // Empty is valid (not required)

    // Validate URL format with protocol
    const urlRegex = /^(https?:\/\/)([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;
    return urlRegex.test(url);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate website if provided
    if (formData.website && !validateWebsite(formData.website)) {
      setFormErrors({
        ...formErrors,
        website: 'Website must be a valid URL with https:// or http:// (e.g., https://overscalesolutions.com)'
      });
      return;
    }

    createOrganization.execute({
      data: formData,
      onSuccess: () => {
        toast.success('Organization created successfully');
        onCreate();
        onClose();
      },
      onError: (error) => {
        toast.error(`Failed to create organization: ${error.message}`);
      }
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div>
        <Label htmlFor="name">Organization Name *</Label>
        <Input
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          required
        />
      </div>
      <div>
        <Label htmlFor="generalInformation">Description</Label>
        <Textarea
          id="generalInformation"
          name="generalInformation"
          value={formData.generalInformation}
          onChange={handleChange}
          rows={3}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="contactEmail">Contact Email</Label>
          <Input
            id="contactEmail"
            name="contactEmail"
            type="email"
            value={formData.contactEmail}
            onChange={handleChange}
          />
        </div>
        <div>
          <Label htmlFor="contactPhone">Contact Phone</Label>
          <Input
            id="contactPhone"
            name="contactPhone"
            value={formData.contactPhone}
            onChange={handleChange}
          />
        </div>
      </div>

      <div>
        <Label htmlFor="website">Website (e.g., https://overscalesolutions.com)</Label>
        <Input
          id="website"
          name="website"
          value={formData.website}
          onChange={handleChange}
        />
        {formErrors.website && (
          <p className="text-sm text-red-500 mt-1">{formErrors.website}</p>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div>
          <Label htmlFor="billingEmail">Billing Email</Label>
          <Input
            id="billingEmail"
            name="billingEmail"
            type="email"
            value={formData.billingEmail}
            onChange={handleChange}
          />
        </div>
        <div>
          <Label htmlFor="billingName">Billing Contact Name</Label>
          <Input
            id="billingName"
            name="billingName"
            value={formData.billingName}
            onChange={handleChange}
          />
        </div>
      </div>

      <div className="flex justify-end space-x-2 pt-4">
        <Button variant="outline" onClick={onClose}>
          Cancel
        </Button>
        <Button type="submit">Create Organization</Button>
      </div>
    </form>
  );
};

// Organization Details Component
const OrganizationDetails = ({ organization, onUpdate, onBack }) => {
  const [activeTab, setActiveTab] = useState('general');
  const [members, setMembers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({
    website: ''
  });
  const [editData, setEditData] = useState({
    general: {
      name: organization?.name || '',
      generalInformation: organization?.generalInformation || '',
    },
    contact: {
      contactEmail: organization?.contactEmail || '',
      contactPhone: organization?.contactPhone || '',
      website: organization?.website || '',
      address: organization?.address || {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
    },
    billing: {
      billingEmail: organization?.billingEmail || '',
      billingName: organization?.billingName || '',
      billingPlan: organization?.billingPlan || 'free',
      billingAddress: organization?.billingAddress || {
        street: '',
        city: '',
        state: '',
        postalCode: '',
        country: ''
      },
    }
  });

  // Debug logging for address data
  useEffect(() => {
    console.log('Organization data:', organization);
    console.log('Address data:', organization?.address);
    console.log('Billing address data:', organization?.billingAddress);
  }, [organization]);

  const [newMemberId, setNewMemberId] = useState('');

  const getMembers = useGetOrganizationMembers() || createDefaultHookResult();
  const updateOrganization = useUpdateOrganization() || createDefaultHookResult();
  const updateBilling = useUpdateOrganizationBilling() || createDefaultHookResult();
  const updateContact = useUpdateOrganizationContact() || createDefaultHookResult();
  const addMember = useAddMemberToOrganization() || createDefaultHookResult();
  const removeMember = useRemoveMemberFromOrganization() || createDefaultHookResult();

  useEffect(() => {
    if (organization?.id) {
      loadMembers();

      // Ensure we update the edit data with the latest organization data
      setEditData({
        general: {
          name: organization.name || '',
          generalInformation: organization.generalInformation || '',
        },
        contact: {
          contactEmail: organization.contactEmail || '',
          contactPhone: organization.contactPhone || '',
          website: organization.website || '',
          address: organization.address || {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: ''
          },
        },
        billing: {
          billingEmail: organization.billingEmail || '',
          billingName: organization.billingName || '',
          billingPlan: organization.billingPlan || 'free',
          billingAddress: organization.billingAddress || {
            street: '',
            city: '',
            state: '',
            postalCode: '',
            country: ''
          },
        }
      });
    }
  }, [organization]);

  const loadMembers = async () => {
    setIsLoading(true);
    getMembers.execute({
      id: organization.id,
      onSuccess: (response) => {
        setMembers(response.data);
        setIsLoading(false);
      },
      onError: (error) => {
        toast.error(`Failed to load organization members: ${error.message}`);
        setIsLoading(false);
      }
    });
  };

  const handleInputChange = (section, field, value) => {
    setEditData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value
      }
    }));

    // Clear website error when field is modified
    if (section === 'contact' && field === 'website' && formErrors.website) {
      setFormErrors({
        ...formErrors,
        website: ''
      });
    }
  };

  const validateWebsite = (url) => {
    if (!url) return true; // Empty is valid (not required)

    // Validate URL format with protocol
    const urlRegex = /^(https?:\/\/)([\w-]+\.)+[\w-]+(\/[\w-./?%&=]*)?$/;
    return urlRegex.test(url);
  };

  const handleAddressChange = (section, field, value) => {
    console.log(`Updating ${section} address field ${field} with value: ${value}`);
    setEditData(prev => ({
      ...prev,
      [section]: {
        ...prev[section],
        [section === 'billing' ? 'billingAddress' : 'address']: {
          ...prev[section][section === 'billing' ? 'billingAddress' : 'address'],
          [field]: value
        }
      }
    }));
  };

  const handleSaveGeneral = async () => {
    updateOrganization.execute({
      id: organization.id,
      data: editData.general,
      onSuccess: () => {
        toast.success('General information updated successfully');
        onUpdate();
      },
      onError: (error) => {
        toast.error(`Failed to update organization: ${error.message}`);
      }
    });
  };

  const handleSaveContact = async () => {
    // Validate website if provided
    if (editData.contact.website && !validateWebsite(editData.contact.website)) {
      setFormErrors({
        ...formErrors,
        website: 'Website must be a valid URL with https:// or http:// (e.g., https://overscalesolutions.com)'
      });
      return;
    }

    // Log the data being sent to the API
    console.log('Saving contact data:', editData.contact);

    updateContact.execute({
      id: organization.id,
      data: editData.contact,
      onSuccess: () => {
        toast.success('Contact information updated successfully');
        onUpdate();
      },
      onError: (error) => {
        toast.error(`Failed to update contact information: ${error.message}`);
      }
    });
  };

  const handleSaveBilling = async () => {
    // Log the data being sent to the API
    console.log('Saving billing data:', editData.billing);

    updateBilling.execute({
      id: organization.id,
      data: editData.billing,
      onSuccess: () => {
        toast.success('Billing information updated successfully');
        onUpdate();
      },
      onError: (error) => {
        toast.error(`Failed to update billing information: ${error.message}`);
      }
    });
  };

  const handleAddMember = async () => {
    if (!newMemberId) {
      toast.error('Please enter an email address');
      return;
    }

    addMember.execute({
      id: organization.id,
      data: { email: newMemberId },
      onSuccess: () => {
        toast.success('Member added successfully');
        setNewMemberId('');
        loadMembers();
      },
      onError: (error) => {
        toast.error(`Failed to add member: ${error.message}`);
      }
    });
  };

  const handleRemoveMember = async (userId) => {
    removeMember.execute({
      id: organization.id,
      userId: userId,
      onSuccess: () => {
        toast.success('Member removed successfully');
        loadMembers();
      },
      onError: (error) => {
        toast.error(`Failed to remove member: ${error.message}`);
      }
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <Button variant="outline" onClick={onBack}>
          <ChevronRight className="mr-2 h-4 w-4 rotate-180" /> Back to Organizations
        </Button>
        <h2 className="text-2xl font-bold">{organization.name}</h2>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid grid-cols-4 w-full">
          <TabsTrigger value="general">General</TabsTrigger>
          <TabsTrigger value="contact">Contact</TabsTrigger>
          <TabsTrigger value="billing">Billing</TabsTrigger>
          <TabsTrigger value="members">Members</TabsTrigger>
        </TabsList>

        <TabsContent value="general" className="space-y-4 py-4">
          <div>
            <Label htmlFor="name">Organization Name</Label>
            <Input
              id="name"
              value={editData.general.name}
              onChange={(e) => handleInputChange('general', 'name', e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="generalInformation">Description</Label>
            <Textarea
              id="generalInformation"
              rows={4}
              value={editData.general.generalInformation}
              onChange={(e) => handleInputChange('general', 'generalInformation', e.target.value)}
            />
          </div>
          <Button onClick={handleSaveGeneral}>Save General Information</Button>
        </TabsContent>

        <TabsContent value="contact" className="space-y-4 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="contactEmail">Contact Email</Label>
              <Input
                id="contactEmail"
                type="email"
                value={editData.contact.contactEmail}
                onChange={(e) => handleInputChange('contact', 'contactEmail', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="contactPhone">Contact Phone</Label>
              <Input
                id="contactPhone"
                value={editData.contact.contactPhone}
                onChange={(e) => handleInputChange('contact', 'contactPhone', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="website">Website (e.g., https://overscalesolutions.com)</Label>
            <Input
              id="website"
              value={editData.contact.website}
              onChange={(e) => handleInputChange('contact', 'website', e.target.value)}
            />
            {formErrors.website && (
              <p className="text-sm text-red-500 mt-1">{formErrors.website}</p>
            )}
          </div>

          <div className="border p-4 rounded-lg mt-4">
            <h3 className="text-lg font-medium mb-3">Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="street">Street</Label>
                <Input
                  id="street"
                  value={editData.contact.address?.street || ''}
                  onChange={(e) => handleAddressChange('contact', 'street', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="city">City</Label>
                <Input
                  id="city"
                  value={editData.contact.address?.city || ''}
                  onChange={(e) => handleAddressChange('contact', 'city', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="state">State/Province</Label>
                <Input
                  id="state"
                  value={editData.contact.address?.state || ''}
                  onChange={(e) => handleAddressChange('contact', 'state', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="postalCode">Postal Code</Label>
                <Input
                  id="postalCode"
                  value={editData.contact.address?.postalCode || ''}
                  onChange={(e) => handleAddressChange('contact', 'postalCode', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="country">Country</Label>
                <Input
                  id="country"
                  value={editData.contact.address?.country || ''}
                  onChange={(e) => handleAddressChange('contact', 'country', e.target.value)}
                />
              </div>
            </div>
          </div>

          <Button onClick={handleSaveContact}>Save Contact Information</Button>
        </TabsContent>

        <TabsContent value="billing" className="space-y-4 py-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="billingEmail">Billing Email</Label>
              <Input
                id="billingEmail"
                type="email"
                value={editData.billing.billingEmail}
                onChange={(e) => handleInputChange('billing', 'billingEmail', e.target.value)}
              />
            </div>
            <div>
              <Label htmlFor="billingName">Billing Contact Name</Label>
              <Input
                id="billingName"
                value={editData.billing.billingName}
                onChange={(e) => handleInputChange('billing', 'billingName', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label htmlFor="billingPlan">Billing Plan</Label>
            <select
              id="billingPlan"
              className="w-full p-2 border rounded"
              value={editData.billing.billingPlan}
              onChange={(e) => handleInputChange('billing', 'billingPlan', e.target.value)}
            >
              <option value="free">Free</option>
              <option value="starter">Starter</option>
              <option value="professional">Professional</option>
              <option value="enterprise">Enterprise</option>
            </select>
          </div>

          <div className="border p-4 rounded-lg mt-4">
            <h3 className="text-lg font-medium mb-3">Billing Address</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Label htmlFor="billingStreet">Street</Label>
                <Input
                  id="billingStreet"
                  value={editData.billing.billingAddress?.street || ''}
                  onChange={(e) => handleAddressChange('billing', 'street', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="billingCity">City</Label>
                <Input
                  id="billingCity"
                  value={editData.billing.billingAddress?.city || ''}
                  onChange={(e) => handleAddressChange('billing', 'city', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="billingState">State/Province</Label>
                <Input
                  id="billingState"
                  value={editData.billing.billingAddress?.state || ''}
                  onChange={(e) => handleAddressChange('billing', 'state', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="billingPostalCode">Postal Code</Label>
                <Input
                  id="billingPostalCode"
                  value={editData.billing.billingAddress?.postalCode || ''}
                  onChange={(e) => handleAddressChange('billing', 'postalCode', e.target.value)}
                />
              </div>
              <div>
                <Label htmlFor="billingCountry">Country</Label>
                <Input
                  id="billingCountry"
                  value={editData.billing.billingAddress?.country || ''}
                  onChange={(e) => handleAddressChange('billing', 'country', e.target.value)}
                />
              </div>
            </div>
          </div>

          <Button onClick={handleSaveBilling}>Save Billing Information</Button>
        </TabsContent>

        <TabsContent value="members" className="py-4">
          <div className="mb-6">
            <h3 className="text-lg font-medium mb-3">Add Organization Member</h3>
            <div className="flex items-center gap-3">
              <Input
                placeholder="Enter member's email address"
                value={newMemberId}
                onChange={(e) => setNewMemberId(e.target.value)}
                type="email"
              />
              <Button onClick={handleAddMember}>
                <UserPlus className="mr-2 h-4 w-4" /> Add Member
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Enter a valid email address of an existing user to add them to this organization
            </p>
          </div>

          <div>
            <h3 className="text-lg font-medium mb-3">Organization Members</h3>
            {isLoading ? (
              <div className="flex justify-center p-6">
                <PulsingSquareLoader size={40} /> {/* Replaced RefreshCw with PulsingSquareLoader */}
              </div>
            ) : members.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {members.map((member) => (
                    <TableRow key={member.id}>
                      <TableCell className="font-mono text-xs">{member.id}</TableCell>
                      <TableCell>{member.name}</TableCell>
                      <TableCell>{member.email}</TableCell>
                      <TableCell>
                        {member.id !== organization.owner && (
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => handleRemoveMember(member.id)}
                          >
                            <UserMinus className="h-4 w-4" />
                          </Button>
                        )}
                        {member.id === organization.owner && (
                          <Badge>Owner</Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <p className="text-center p-4 text-muted-foreground">No members found</p>
            )}
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Organizations Management Component
const OrganizationsManagement = () => {
  const [selectedOrganization, setSelectedOrganization] = useState(null);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const user = useUser();

  const {
    organizationsLoading,
    organizationsFetchSuccess,
    organizationsFetchError,
    organizations,
    organizationsErrorMessage,
    fetchOrganizations,
  } = useGetOrganizations((state) => ({
    organizationsLoading: state.organizationsLoading,
    organizationsFetchSuccess: state.organizationsFetchSuccess,
    organizationsFetchError: state.organizationsFetchError,
    organizations: state.organizations,
    organizationsErrorMessage: state.organizationsErrorMessage,
    fetchOrganizations: state.fetchOrganizations,
  }));

  const deleteOrganization = useDeleteOrganization() || createDefaultHookResult();
  const setPrimaryOrganization = useSetPrimaryOrganization() || createDefaultHookResult();
  const getOrganization = useGetOrganization() || createDefaultHookResult();

  // Initial load
  useEffect(() => {
    fetchOrganizations();
  }, []);

  // Handle setting primary organization
  const handleSetPrimary = async (organizationId) => {
    setPrimaryOrganization.execute({
      id: organizationId,
      onSuccess: () => {
        toast.success('Primary organization updated');
        fetchOrganizations();
      },
      onError: (error) => {
        toast.error(`Failed to set primary organization: ${error.message}`);
      }
    });
  };

  // Handle deleting an organization
  const handleDeleteOrganization = async (organizationId) => {
    if (window.confirm('Are you sure you want to delete this organization? This action cannot be undone.')) {
      deleteOrganization.execute({
        id: organizationId,
        onSuccess: () => {
          toast.success('Organization deleted successfully');
          fetchOrganizations();
        },
        onError: (error) => {
          toast.error(`Failed to delete organization: ${error.message}`);
        }
      });
    }
  };

  // Handle selecting an organization for detailed view
  const handleSelectOrganization = (org) => {
    // Get fresh data for the selected organization
    getOrganization.execute({
      id: org.id,
      onSuccess: (response) => {
        setSelectedOrganization(response.data);
      },
      onError: (error) => {
        toast.error(`Failed to load organization details: ${error.message}`);
      }
    });
  };

  // Render loading state
  if (organizationsLoading && (!organizations || organizations.length === 0)) {
    return (
      <div className="flex justify-center items-center h-64">
        <PulsingSquareLoader size={40} /> {/* Replaced RefreshCw with PulsingSquareLoader */}
      </div>
    );
  }

  // Render error state
  if (organizationsFetchError && (!organizations || organizations.length === 0)) {
    return (
      <div className="text-center p-6 text-red-500">
        <p>{organizationsErrorMessage}</p>
        <Button
          variant="outline"
          onClick={fetchOrganizations}
          className="mt-4"
        >
          <PulsingSquareLoader size={16} /> {/* Replaced RefreshCw with PulsingSquareLoader */}
          <span className="ml-2">Try Again</span> {/* Added ml-2 for spacing if needed */}
        </Button>
      </div>
    );
  }

  // Render organization details view if an organization is selected
  if (selectedOrganization) {
    return (
      <OrganizationDetails
        organization={selectedOrganization}
        onBack={() => setSelectedOrganization(null)}
        onUpdate={fetchOrganizations}
      />
    );
  }

  // Render organizations list
  return (
    <div>
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Create New Organization</DialogTitle>
            <DialogDescription>
              Create a new organization to manage projects and team members.
            </DialogDescription>
          </DialogHeader>
          <NewOrganizationForm
            onClose={() => setIsCreateModalOpen(false)}
            onCreate={fetchOrganizations}
          />
        </DialogContent>
      </Dialog>

      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-lighter">Your Organizations</h2>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> New Organization
        </Button>
      </div>

      {(!organizations || organizations.length === 0) ? (
        <div className="text-center p-12 border rounded-lg">
          <Building className="h-12 w-12 mx-auto mb-4 text-gray-400" />
          <h3 className="text-xl font-semibold mb-2">No Organizations Yet</h3>
          <p className="mb-4 text-muted-foreground">
            Create your first organization to start collaborating with your team.
          </p>
          <Button onClick={() => setIsCreateModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" /> Create Organization
          </Button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4">
          {organizations.map((org) => (
            <OrganizationCard
              key={org.id}
              organization={org}
              isPrimary={user.organizationId === org.id}
              onSelect={handleSelectOrganization}
              onSetPrimary={handleSetPrimary}
              onDelete={handleDeleteOrganization}
            />
          ))}
        </div>
      )}
    </div>
  );
};

function Dashboard() {
  const [searchParams] = useSearchParams()
  const [showAnimation, setShowAnimation] = useState(true)
  const user = useUser()
  //console.log('Dashboard:user', user)

  const [showReleaseNotesModal, setShowReleaseNotesModal] = useState(false);
  const { settings, updateReleaseNotesHiddenDate, fetchAccountInfo } = useAccountStore();
  const latestReleaseNoteDate = new Date(releaseNotesData[releaseNotesData.length - 1]?.date);
  const hideReleaseNotes = !!settings?.releaseNotesHiddenDate && new Date(settings.releaseNotesHiddenDate) >= latestReleaseNoteDate;
  const hasFetchedAccountInfo = useRef(false); // Use ref to ensure single fetch
  // Fetch account info and set initial modal state
  useEffect(() => {
    if (!hasFetchedAccountInfo.current) {
      console.log("Fetching account info - runs once");
      hasFetchedAccountInfo.current = true;
      fetchAccountInfo().then((response) => {
        console.log('fetchAccountInfo response:', response); // Log raw response
        const hiddenDate = settings?.releaseNotesHiddenDate ? new Date(settings.releaseNotesHiddenDate) : null;
        const hasNewNotes = releaseNotesData.some(
          (note) => !hiddenDate || new Date(note.date) > hiddenDate
        );
        const hideReleaseNotes = !!settings?.releaseNotesHiddenDate && new Date(settings.releaseNotesHiddenDate) >= latestReleaseNoteDate;

        console.log('Initial evaluation after fetch:');
        console.log('hiddenDate:', hiddenDate);
        console.log('hasNewNotes:', hasNewNotes);
        console.log('hideReleaseNotes:', hideReleaseNotes);

        setShowReleaseNotesModal(!hideReleaseNotes && hasNewNotes);
      }).catch((error) => {
        console.error('Failed to fetch account info:', error);
        setShowReleaseNotesModal(false);
      });
    }
  }, [fetchAccountInfo]);


  const [showWelcomeMessage, setShowWelcomeMessage] = useState(true)
  const [isLoaded, setIsLoaded] = useState(false)

  // Add a state to track the project being refreshed
  const [refreshProjectId, setRefreshProjectId] = useState<string | null>(null)

  // Add state for animating project cards
  const [animatedCards, setAnimatedCards] = useState<Record<string, boolean>>(
    {}
  )


  // Track if the intro animation is completed
  const [introAnimationComplete, setIntroAnimationComplete] = useState(false)
  // Add state for animating buttons
  const [animatedButtons, setAnimatedButtons] = useState<
    Record<string, boolean>
  >({})
  // Add state for tracking exit animations
  const [exitAnimationActive, setExitAnimationActive] = useState(false)
  // Add state to track which project is being navigated to
  const [navigatingToProject, setNavigatingToProject] = useState<any>(null)
  const [selectedProjectId, setSelectedProjectId] = useState<any>(null)
  // Flag to ensure we only navigate once
  const isNavigatingRef = useRef(false)

  // Add state to track element colors
  const [elementColors, setElementColors] = useState({})

  // Add state to control color animations
  const [colorfulMode, setColorfulMode] = useState(true)

  useEffect(() => {
    const savedWelcomeState = localStorage.getItem('showWelcomeMessage')
    if (savedWelcomeState !== null) {
      setShowWelcomeMessage(JSON.parse(savedWelcomeState))
    }
    setIsLoaded(true)
  }, [])

  useEffect(() => {
    console.log('Dashboard mounted')
    return () => console.log('Dashboard unmounted')
  }, [])

  useEffect(() => {
    if (isLoaded) {
      localStorage.setItem(
        'showWelcomeMessage',
        JSON.stringify(showWelcomeMessage)
      )
    }
  }, [showWelcomeMessage, isLoaded])

  const [selectedMenuItem, setSelectedMenuItem] = useState('projects')
  const [selectedAdminMenuItem, setSelectedAdminMenuItem] = useState(null)

  const [versionGlitch, setVersionGlitch] = useState(true)

  useEffect(() => {
    const noAnim = searchParams.get('noAnim')
    if (noAnim === 'true') {
      setShowAnimation(false)
      // If we're skipping the intro animation, mark it as complete immediately
      setIntroAnimationComplete(true)
    }
  }, [searchParams])

  // Add effect for version glitch
  useEffect(() => {
    if (versionGlitch) {
      const timer = setTimeout(() => {
        setVersionGlitch(false)
      }, 2000) // Let version number glitch a bit longer

      return () => clearTimeout(timer)
    }
  }, [versionGlitch])

  // Handle the completion of the SpechopsAnimation
  const handleAnimationComplete = useCallback(() => {
    console.log('Intro animation complete')
    setIntroAnimationComplete(true)
  }, [])

  // Hook to fetch projects
  const {
    projectsLoading,
    projectsFetchSuccess,
    projectsFetchError,
    projects,
    projectsErrorMessage,
    fetchProjects,
  } = useGetProjects((state) => ({
    projectsLoading: state.projectsLoading,
    projectsFetchSuccess: state.projectsFetchSuccess,
    projectsFetchError: state.projectsFetchError,
    projects: state.projects,
    projectsErrorMessage: state.projectsErrorMessage,
    fetchProjects: state.fetchProjects,
  }))

  // Memoize fetchProjects so that its reference won't change on every render
  const memoizedFetchProjects = useCallback(() => {
    fetchProjects()
  }, [fetchProjects])

  const hasFetched = useRef(false)

  useEffect(() => {
    if (user.id && !hasFetched.current) {
      memoizedFetchProjects()
      hasFetched.current = true
    }
  }, [user.id])

  const deleteProject = (useDeleteProject() ||
    createDefaultHookResult())

  const getProject = (useGetProject() ||
    createDefaultHookResult())

  const [isModalOpen, setModalOpen] = useState(false)


  /*   console.log('Settings:', settings);
    console.log('releaseNotesHiddenDate:', settings?.releaseNotesHiddenDate);
    console.log('Latest Release Note Date:', latestReleaseNoteDate);
    console.log('hideReleaseNotes:', hideReleaseNotes);
    console.log('showWelcomeMessage:', showWelcomeMessage);
    console.log('selectedMenuItem:', selectedMenuItem);
    console.log('selectedAdminMenuItem:', selectedAdminMenuItem);
    console.log('showReleaseNotes:', showReleaseNotes); */


  // When refreshProjectId changes, fetch the updated project data
  useEffect(() => {
    if (refreshProjectId) {
      getProject.execute({
        id: refreshProjectId,
        onSuccess: (response) => {
          // Update the project in the projects array
          if (projects && Array.isArray(projects)) {
            const updatedProjects = [...projects]
            const projectIndex = updatedProjects.findIndex(
              (p) => p.id === refreshProjectId
            )

            if (projectIndex !== -1) {
              updatedProjects[projectIndex] = response.data.project
              // Update the projects array with the new project data
              useGetProjects.setState({
                projects: updatedProjects,
                projectsFetchSuccess: true,
              })
            }
          }
          setRefreshProjectId(null) // Reset after refresh
        },
        onError: (error) => {
          console.error('Error refreshing project:', error)
          toast.error('Failed to refresh project data')
          setRefreshProjectId(null) // Reset after error
        },
      })
    }
  }, [refreshProjectId])

  // Start animation for project cards ONLY when both:
  // 1. Projects are loaded successfully
  // 2. The intro animation has completed (or was skipped)
  useEffect(() => {
    if (introAnimationComplete) {
      // First animate the buttons
      const menuButtons = ['organizations', 'projects', 'subscription', 'release notes', 'settings']
      const adminButtons = user.isAdmin
        ? [
          'models',
          'documents',
          'langflow',
          'users',
          'admin-projects',
          'statistics',
          'event-log',
        ]
        : []

      const allButtons = [...menuButtons, ...adminButtons, 'newProject']

      const buttonAnimations = {}
      const buttonColors = {}
      const buttonDelay = 50 // Initial delay for buttons

      allButtons.forEach((button, index) => {
        // Generate random color for each button
        buttonColors[button] = getRandomColorClass()

        // Set a timeout to animate each button with increasing delay
        setTimeout(
          () => {
            setAnimatedButtons((prev) => ({
              ...prev,
              [button]: true,
            }))
          },
          buttonDelay * (index + 1)
        )

        // Track that this button should be animated
        buttonAnimations[button] = false
      })

      // Initialize all buttons as not animated yet
      setAnimatedButtons(buttonAnimations)
      // Set random colors for buttons
      setElementColors((prev) => ({ ...prev, ...buttonColors }))

      // Then animate the project cards if they're loaded
      if (
        projects &&
        Array.isArray(projects) &&
        projects.length > 0 &&
        projectsFetchSuccess
      ) {
        // Buttons are done, now animate cards with a staggered delay
        // Start cards after all buttons are animated
        const cardsStartDelay = buttonDelay * (allButtons.length + 2)
        const cardDelay = 50 // Initial delay for cards (doubled speed)

        const cardAnimations = {}
        const cardColors = {}

        projects.forEach((project, index) => {
          // Generate random color for each card
          cardColors[project.id] = getRandomColorClass()

          // Set a timeout to animate each card with increasing delay
          setTimeout(
            () => {
              setAnimatedCards((prev) => ({
                ...prev,
                [project.id]: true,
              }))
            },
            cardsStartDelay + cardDelay * (index + 1)
          )

          // Track that this card should be animated (once the timeout completes)
          cardAnimations[project.id] = false
        })

        // Initialize all cards as not animated yet
        setAnimatedCards(cardAnimations)
        // Set random colors for cards
        setElementColors((prev) => ({ ...prev, ...cardColors }))
      }
    }
  }, [introAnimationComplete, user.isAdmin])

  // Animate project cards when projects are loaded
  // Animate project cards when projects are loaded
  useEffect(() => {
    if (
      introAnimationComplete &&
      projects &&
      Array.isArray(projects) &&
      projects.length > 0 &&
      projectsFetchSuccess
    ) {
      const newCardAnimations = {};
      const cardColors = {};
      const cardDelay = 50; // Initial delay for cards
      const cardsStartDelay = 100; // Small delay to ensure buttons are done

      projects.forEach((project, index) => {
        // Generate random color for each card
        cardColors[project.id] = getRandomColorClass();

        // Only animate if not already animated
        if (!animatedCards[project.id]) {
          setTimeout(() => {
            setAnimatedCards((prev) => ({
              ...prev,
              [project.id]: true,
            }));
          }, cardsStartDelay + cardDelay * (index + 1));
        }

        // Track cards to initialize animation state
        newCardAnimations[project.id] = animatedCards[project.id] || false;
      });

      // Update element colors for cards
      setElementColors((prev) => ({ ...prev, ...cardColors }));
    }
  }, [introAnimationComplete, projects, projectsFetchSuccess]); // Removed animatedCards from dependencies

  // Track if the animation has been rendered at least once
  const animationHasRendered = useRef(false)

  // Navigate to Flow after exit animation completes
  useEffect(() => {
    if (
      exitAnimationActive &&
      navigatingToProject &&
      !isNavigatingRef.current
    ) {
      // Set the navigation flag to prevent multiple navigations
      isNavigatingRef.current = true

      console.log('Exit animation activated, waiting before navigation...')

      // Set a much longer fixed timeout to ensure animation is visible
      // This helps ensure the browser actually shows the animation
      const navigateTimeout = setTimeout(() => {
        console.log('Navigation timeout completed, proceeding to navigate')

        // Reset the exit animation flag
        setExitAnimationActive(false)

        const url = `/flow/${navigatingToProject.id}?projectName=${encodeURIComponent(
          navigatingToProject.projectName
        )}&docName=${encodeURIComponent(navigatingToProject.docName)}`

        // Navigate to the flow page
        window.location.href = url
      }, 400) // Significantly longer timeout to ensure animation visibility

      return () => {
        clearTimeout(navigateTimeout)
        isNavigatingRef.current = false
      }
    }
  }, [exitAnimationActive, navigatingToProject])

  const toggleModal = () => setModalOpen(!isModalOpen)
  const onCreateNewProject = (statusMessage, status) => {
    console.log('onCreateNewProject:', statusMessage, status)
    if (status === 'success') {
      console.log('Reloading projects...')
      fetchProjects()
    }
    toast.info(statusMessage)
  }

  useEffect(() => {
    console.log('Dashboard:projects', projects)
  }, [projects])

  // Function to handle navigation to flow with exit animation
  const onNavigate = async (project) => {
    try {
      // Prevent navigation if already navigating
      if (isNavigatingRef.current || exitAnimationActive) return

      console.log(
        'Starting navigation sequence for project:',
        project.projectName
      )

      // Reset animation tracking
      animationHasRendered.current = false

      // Show loading toast
      toast.info(`Loading project ${project.projectName}`)

      // Set the project we're navigating to
      setNavigatingToProject(project)

      // Force a layout reflow before applying the animation
      // This helps ensure the browser actually shows the animation
      document.body.offsetHeight

      console.log('Activating exit animation')
      // Directly set the exit animation flag
      setExitAnimationActive(true)

      // The actual navigation will happen in the useEffect above after animation completes
    } catch (error) {
      console.error('Failed to initialize store:', error)
      toast.error('Error navigating to project')
      isNavigatingRef.current = false
      setExitAnimationActive(false)
    }
  }

  // Handler for when project settings are updated
  const handleProjectUpdate = (projectId) => {
    setRefreshProjectId(projectId)
    toast.info('Refreshing project data')
  }

  // Render functions

  const WelcomeMessage = () => (
    <div className={styles.welcomeMessage}>
      <h1>Welcome to Spechops</h1>
      <p>Your streamlined business development journey starts here.</p>
      <p>
        <b>Create a Project:</b> Jumpstart your ideas in just hours. It's quick,
        intuitive, and sets the foundation for what's next. Why It Matters:
        Engage more bidders, refine requirements, and use AI to make smarter
        decisions—faster and more effectively than ever.
      </p>
      <p>
        <b>Your Gain:</b> A smoother path to development, competitive bidding,
        and a robust final product. Let's innovate together.
      </p>
      <Button
        onClick={() => setShowWelcomeMessage(false)}
        className="p-button-secondary"
        style={{ marginTop: '10px' }}
      >
        Thank you, I understand
      </Button>
      <hr
        style={{
          margin: '20px 0',
          border: 'none',
          borderTop: '1px solid #ddd',
        }}
      />
    </div>
  )

  const renderLoading = () => {
    return (
      <div className="flex justify-center items-center h-64">
        <PulsingSquareLoader size={32} /> {/* Replaced RefreshCw with PulsingSquareLoader */}
      </div>
    );
  }

  const renderError = () => <p>Error fetching data: {projectsErrorMessage}</p>

  const renderAdminMenuContent = () => {
    switch (selectedAdminMenuItem) {
      case 'models':
        return <ModelManager />
      case 'documents':
        return <DocumentManager />
      case 'langflow':
        return <LangflowManager />
      case 'users':
        return <h2>Users</h2>
      case 'admin-projects':
        return <h2>Projects (Admin View)</h2>
      case 'statistics':
        return <h2>Statistics</h2>
      case 'event-log':
        return <h2>Event Log</h2>
      default:
        return null
    }
  }

  const renderProjects = () => (
    <>
      <Dialog open={isModalOpen} onOpenChange={setModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create New Project</DialogTitle>
          </DialogHeader>
          <NewProjectForm
            userId={user.id}
            userEmail={user.email}
            closeModal={toggleModal}
            onCreateNewProject={onCreateNewProject}
          />
        </DialogContent>
      </Dialog>

      <div style={{ marginBottom: '20px' }}>
        {(projects || []).map((project, index) => (
          <div
            key={project.id}
            className={`${styles.projectCard} ${animatedCards[project.id] ? styles.projectCardAnimated : ''
              } ${exitAnimationActive ? styles.exitAnimation : ''} ${colorfulMode && elementColors[project.id] ? styles.colorful : ''
              } ${colorfulMode && elementColors[project.id]
                ? styles[elementColors[project.id]]
                : ''
              }`}
            style={{
              animationDelay: `${index * 75}ms`, // Staggered animation delay (doubled speed)
            }}
          >
            <div className={`${styles.project_card_wrapper} ${colorfulMode && elementColors[project.id] ? styles[elementColors[project.id]] : ''
              } ${!exitAnimationActive && animatedCards[project.id] ? styles.gentleWaveAnimation : ''
              }`}>
              <ProjectCard
                key={project.id}
                project={project}
                userId={user.id}
                onNavigate={() => onNavigate(project)}
                onDelete={(projectId) => {
                  if (
                    window.confirm(
                      'Are you sure you want to delete this project?'
                    )
                  ) {
                    toast.info(`Deleting project: ${project.projectName}`)
                    deleteProject.execute({
                      id: projectId,
                      onSuccess: (response) => {
                        console.log('Project deleted successfully:', response)
                        toast.success(
                          `Project deleted successfully: ${project.projectName}`
                        )
                        fetchProjects()
                      },
                      onError: (error) => {
                        console.error('Error creating project:', error)
                        toast.error(
                          `Error deleting project ${project.projectName}`
                        )
                      },
                    })
                  }
                }}
                onOpen={(projectId, projectName) => {
                  toast.info(`Loading project ${projectName}`)
                }}
                onSettingsChanged={() => handleProjectUpdate(project.id)}
              />
            </div>
          </div>
        ))}
      </div>
    </>
  )

  const renderMenuContent = () => {
    switch (selectedMenuItem) {
      case 'organizations':
        return <OrganizationsManagement />;
      case 'projects':
        return (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 className="text-2xl font-light pl-6">Projects</h2>
              <div className={`${styles.animatedButton} ${animatedButtons.newProject ? styles.animatedButtonActive : ''} ${exitAnimationActive ? styles.exitAnimation : ''} ${!exitAnimationActive && animatedButtons.newProject ? styles.gentleWaveAnimation : ''
                } ${colorfulMode && elementColors.newProject ? styles.colorful : ''} ${colorfulMode && elementColors.newProject ? styles[elementColors.newProject] : ''}`}>
                <Button onClick={toggleModal}>
                  <Plus className="mr-2 h-4 w-4" />
                  New Project
                </Button>
              </div>
            </div>
            {projectsLoading && renderLoading()}
            {projectsFetchError && renderError()}
            {projectsFetchSuccess && renderProjects()}
          </>
        );
      case 'subscription':
        return (
          <>
            <h2 className="text-2xl font-lighter mb-6">
              {user?.organizationId ? 'Organization Subscription & Usage' : 'Subscription & Usage'}
            </h2>
            <SubscriptionManager organizationId={user?.organizationId} />
          </>
        );
      case 'settings':
        return <AccountSettings />;
      case 'release notes': // Handle inline view
        return <ReleaseNotesViewer notes={releaseNotesData} />;
      default:
        return null;
    }
  };

  // Update the other buttons to use color classes

  // Main render
  return (
    <div className={styles.dashboardContainer}>
      {showAnimation && (
        <SpechopsAnimation onAnimationComplete={handleAnimationComplete} />
      )}
      <Toaster />
      <div
        className={`fixed right-0.5 bottom-0.5 z-10 text-xs opacity-20 select-none ${versionGlitch ? 'version-glitch' : ''}`}
      >
        {getVersionString()}
      </div>
      <OverlayTopBar
        showStartContent={true}
        showLogo={true}
        showDashboardButton={false}
        userName={user.name}
      />
      <div className={styles.dashboardContent}>
        <div className={styles.contentWrapper}>
          {showWelcomeMessage && <WelcomeMessage />}
          {showReleaseNotesModal && !hideReleaseNotes && (
            <ReleaseNotes
              notes={releaseNotesData}
              releaseNotesHiddenDate={settings?.releaseNotesHiddenDate} // Pass the hidden date
              onClose={() => {
                console.log('Closing release notes modal');
                setShowReleaseNotesModal(false);
              }}
              onHideLatest={() => {
                console.log('Dismissing release notes');
                updateReleaseNotesHiddenDate(latestReleaseNoteDate.toISOString()).then(() => {
                  console.log('Dismiss complete, hiding modal');
                  setShowReleaseNotesModal(false);
                  fetchAccountInfo(); // Refetch to ensure store syncs with server
                });
              }}
            />
          )}
          {user.isAdmin && (
            <>
              <div className={styles.adminMenuContainer}>
                {[
                  'documents',
                  'langflow',
                  'users',
                  'admin-projects',
                  'statistics',
                  'event-log',
                ].map((item) => (
                  <span
                    key={item}
                    className={`${styles.animatedButton} ${animatedButtons[item] ? styles.animatedButtonActive : ''
                      } ${exitAnimationActive ? styles.exitAnimation : ''} ${colorfulMode && elementColors[item] ? styles.colorful : ''
                      } ${colorfulMode && elementColors[item]
                        ? styles[elementColors[item]]
                        : ''
                      }`}
                    style={{ marginRight: '10px' }}
                  >
                    <Button
                      onClick={() => setSelectedAdminMenuItem(item)}
                      className={
                        selectedAdminMenuItem === item
                          ? 'p-button-secondary'
                          : ''
                      }
                    >
                      {item === 'admin-projects'
                        ? 'Projects'
                        : item.charAt(0).toUpperCase() + item.slice(1)}
                    </Button>
                  </span>
                ))}
              </div>
              {selectedAdminMenuItem && (
                <div className={styles.adminContent}>
                  {renderAdminMenuContent()}
                </div>
              )}
              <hr
                style={{
                  margin: '20px 0',
                  border: 'none',
                  borderTop: '1px solid #ddd',
                }}
              />
            </>
          )}
          <div className={styles.menuContainer}>
            {['organizations', 'projects', 'subscription', 'release notes'].map((item) => (
              <span
                key={item}
                className={`${styles.animatedButton} ${animatedButtons[item] ? styles.animatedButtonActive : ''
                  } ${exitAnimationActive ? styles.exitAnimation : ''} ${!exitAnimationActive && animatedButtons[item] ? styles.gentleWaveAnimation : ''
                  } ${colorfulMode && elementColors[item] ? styles.colorful : ''
                  } ${colorfulMode && elementColors[item]
                    ? styles[elementColors[item]]
                    : ''
                  }`}
                style={{ marginRight: '10px' }}
              >
                <Button
                  onClick={() => {
                    if (item === 'release notes') {
                      setSelectedMenuItem('release notes'); // Show inline viewer
                      setShowReleaseNotesModal(false); // Ensure modal doesn't show
                    } else {
                      setSelectedMenuItem(item);
                      setShowReleaseNotesModal(false);
                    }
                  }}
                  className={selectedMenuItem === item ? 'p-button-secondary' : ''}
                >
                  {item.charAt(0).toUpperCase() + item.slice(1)}
                </Button>
              </span>
            ))}
            <span
              key="accountsettings"
              className={`${styles.animatedButton} ${animatedButtons.settings ? styles.animatedButtonActive : ''
                } ${exitAnimationActive ? styles.exitAnimation : ''} ${!exitAnimationActive && animatedButtons.settings ? styles.gentleWaveAnimation : ''
                } ${colorfulMode && elementColors.settings ? styles.colorful : ''
                } ${colorfulMode && elementColors.settings
                  ? styles[elementColors.settings]
                  : ''
                }`}
            >
              <Button
                onClick={() => {
                  setSelectedMenuItem('settings');
                  setShowReleaseNotesModal(false);
                }}
                className={selectedMenuItem === 'settings' ? 'p-button-secondary' : ''}
              >
                <Settings style={{ width: '10px', height: '10px' }} />
                Account
              </Button>
            </span>
          </div>
          <hr
            style={{
              margin: '20px 0',
              border: 'none',
              borderTop: '1px solid #ddd',
            }}
          />
          {renderMenuContent()}
        </div>
      </div>

    </div>
  )
}


// Memoize the component to prevent unnecessary re-renders
export default memo(Dashboard)
